// Оформление таблицы — эталонная дизайн-система (Figma, фрейм «таблица»).
// Стиль ровно один (решение 8 в docs/superpowers/specs/2026-08-06-lesson-design-system-design.md):
// дизайнер не давала вариантов, а из студии их и не было чем выбрать — в
// TableSettings.tsx контрола стиля нет, только счётчики строк/столбцов. Раньше
// здесь было три ветки (`simple`/`striped`/`bordered`) под поле `tableStyle`,
// но достижима была ровно одна — остальные были мёртвым кодом. Само поле в
// типе (`DesignTable.tableStyle`) осталось: см. write-only комментарий там же.
import type { CSSProperties } from 'react'
import { blockTheme } from '@teach/ui'

const db = blockTheme.designBlock

/** Рамка и разделители — 0.7 (3 px макета ÷ 4.24). */
const BORDER = `0.7px solid ${db.tableBorder}`

/** Авторазмер: чем больше столбцов, тем мельче текст — 12 / 14 / 16, как в
 *  мобилке (16 при ≤2 колонках — по живому экрану). Кегль без line-height
 *  Tailwind (`text-sm` и т.п. несут свой): строка — `normal` канваса, 1.26. */
export const tableTextSizeClass = (cols: number): string =>
  cols >= 4 ? 'text-[12px]' : cols === 3 ? 'text-[14px]' : 'text-[16px]'

/** Поля ячейки: сбоку — линия текста (16, как у текста в подложке), сверху и
 *  снизу 14 — строка ~48 при тексте 16 (в макете 39.2 при 14 — на экране
 *  читалось мелко, мобилка взяла крупнее). Рамка таблицы при этом стоит по
 *  краю элемента, как картинка и подложки. */
export const TABLE_CELL_CLASS = 'px-4 py-[14px] align-middle'

/** Внешняя рамка со скруглением; `overflow-hidden` на носителе режет углы ячеек. */
export const TABLE_FRAME_STYLE: CSSProperties = {
  border: BORDER,
  borderRadius: '7px',
  color: db.text,
}

/** Полосатость считается от индекса строки; шапка (0) свой фон берёт из ячеек и
 *  чередованием никогда не красится. */
export const tableRowStyle = (row: number): CSSProperties =>
  row === 0 ? {} : { backgroundColor: row % 2 === 0 ? db.tableRowAltBg : db.tableRowBg }

export interface CellPosition {
  row: number
  rowCount: number
  col: number
  colCount: number
}

/** Разделители — между строками (не после последней) И между колонками (не
 *  после последней). Вертикальных в макете нет — добавлены решением дизайнера. */
export const tableCellStyle = (at: CellPosition): CSSProperties => ({
  ...(at.row === 0 ? { backgroundColor: db.tableHeaderBg } : {}),
  ...(at.row < at.rowCount - 1 ? { borderBottom: BORDER } : {}),
  ...(at.col < at.colCount - 1 ? { borderRight: BORDER } : {}),
})
